import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";

/**
 * Admin page: monitor issued gift cards and redemption status.
 * When purchaser claims recipient has not received, check here:
 * - Redeemed = cannot resend (only option was resend; no other changes allowed).
 * - Not redeemed = can resend.
 */
export default function GiftCardsList() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [redeemedFilter, setRedeemedFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [resendingId, setResendingId] = useState(null);

  const fetchGiftCards = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextLimit = opts.limit ?? limit;

    setLoading(true);
    setErr("");

    try {
      const res = await api.get("/admin/gift-cards", {
        params: {
          search: search || undefined,
          redeemed: redeemedFilter !== "" ? redeemedFilter : undefined,
          page: nextPage,
          limit: nextLimit,
        },
      });

      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load gift cards";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGiftCards({ page: 1 });
  }, []);

  const onApply = (e) => {
    e?.preventDefault?.();
    setPage(1);
    fetchGiftCards({ page: 1 });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchGiftCards({ page: 1 });
    }, 400);
    return () => clearTimeout(t);
  }, [search, redeemedFilter]);

  const handleResend = async (row) => {
    if (row.redeemed) {
      toast.error("Cannot resend: gift card already redeemed.");
      return;
    }
    try {
      setResendingId(row.id);
      await api.post(`/admin/gift-cards/${row.id}/resend`);
      toast.success("Gift card email resent successfully.");
      await fetchGiftCards({ page: meta.page });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Resend failed");
    } finally {
      setResendingId(null);
    }
  };

  const goPrev = () => {
    if (meta.page <= 1 || loading) return;
    const p = meta.page - 1;
    setPage(p);
    fetchGiftCards({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages || loading) return;
    const p = meta.page + 1;
    setPage(p);
    fetchGiftCards({ page: p });
  };

  const formatMoney = (n) =>
    Number(n) != null && !Number.isNaN(Number(n))
      ? `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "-";

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : "-");

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Gift Cards</h1>
          {/* <p className="text-sm text-gray-600 mt-1">
            Monitor issued gift cards and redemption status. If purchaser claims recipient did not receive: only
            <strong> resend</strong> is allowed, and only when the card has <strong>not</strong> been redeemed.
          </p> */}
        </div>
      </div>

      <form onSubmit={onApply} className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Order ID, code, recipient email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[180px]">
          <label className="text-sm font-semibold">Redemption status</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={redeemedFilter}
            onChange={(e) => setRedeemedFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="0">Not redeemed</option>
            <option value="1">Redeemed</option>
          </select>
        </div>

        <div className="w-full sm:w-auto sm:min-w-[120px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={(e) => {
              const v = Number(e.target.value) || 20;
              setLimit(v);
              setPage(1);
              fetchGiftCards({ page: 1, limit: v });
            }}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <button
          type="submit"
          className="w-full sm:w-auto rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
        >
          Apply
        </button>
      </form>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Issued gift cards</span>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>
            <button
              type="button"
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No gift cards found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-t bg-white">
                <tr className="text-left">
                  <th className="px-4 py-3">Order ID</th>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-4 py-3">Sender email</th>
                  <th className="px-4 py-3">Valid till</th>
                  <th className="px-4 py-3">Redeemed</th>
                  <th className="px-4 py-3">Redeemed at</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="border-t">
                {rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-semibold">{r.giftcard_order_id || "-"}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{r.code_masked ?? r.code ?? "-"}</td>
                    <td className="px-4 py-3">{formatMoney(r.amount)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.recipient_name || "-"}</div>
                      <div className="text-xs text-gray-600">{r.recipient_email || "-"}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.sender_recipient_email || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">{formatDate(r.valid_till)}</td>
                    <td className="px-4 py-3">
                      {r.redeemed ? (
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-amber-100 text-amber-800">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{formatDate(r.redeemed_at)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(r.created_at)}</td>
                    <td className="px-4 py-3">
                      {r.redeemed ? (
                        <span className="text-xs text-gray-500">—</span>
                      ) : (
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50"
                          onClick={() => handleResend(r)}
                          disabled={resendingId === r.id}
                        >
                          {resendingId === r.id ? "Sending…" : "Resend email"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
